
import React from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import rehypeRaw from 'rehype-raw';
import { OptimizedImage } from '@/components/OptimizedImage';
import { BlogFeaturedImage } from '@/components/blog/BlogFeaturedImage';
import { publicBlogImageExists } from '@/lib/publicBlogImages';
import { useParams, Link } from 'react-router-dom';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { SEOHead } from '@/components/SEOHead';
import { NewsletterSignup } from '@/components/NewsletterSignup';
import { Calendar, ArrowLeft, ArrowRight, Star, Camera, Target } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useHydrated } from '@/hooks/use-hydrated';
import { getPostBySlug, getRelatedPosts } from '@/lib/blog';
import type { BlogPost as BlogPostType } from '@/lib/blog';

function formatCategoryName(slug: string): string {
  return slug
    .split('-')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

const BlogPost: React.FC = () => {
  const { user, isSubscribed, loading } = useAuth();
  const hydrated = useHydrated();
  const showSubscriberCTA = hydrated && !loading && !!user && isSubscribed;
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (!post) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
          <div className="max-w-3xl mx-auto mt-8 text-center">
            <h1 className="text-3xl font-bold mb-4 text-foreground">Post Not Found</h1>
            <p className="text-muted-foreground mb-6">
              The blog post you're looking for doesn't exist or has been removed.
            </p>
            <Link to="/blog">
              <Button>
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>
          </div>
        </div>
      </div>
    );
  }

  const heroReady = !!post.featured_image && publicBlogImageExists(post.featured_image);

  // Build article structured data
  const articleStructuredData = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    "headline": post.title,
    "description": post.excerpt || post.seo_description || '',
    "image": heroReady ? post.featured_image : undefined,
    "datePublished": post.published_at,
    "dateModified": post.published_at,
    "author": {
      "@type": "Person",
      "name": "Eric D. Brown, D.Sc.",
      "url": "https://ericbrown.com"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Milky Way Planner",
      "url": "https://www.milkywayplanner.com"
    },
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": `https://www.milkywayplanner.com/blog/${post.slug}`
    }
  };

  const breadcrumbStructuredData = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Home",
        "item": "https://www.milkywayplanner.com"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Blog",
        "item": "https://www.milkywayplanner.com/blog"
      },
      {
        "@type": "ListItem",
        "position": 3,
        "name": post.title,
        "item": `https://www.milkywayplanner.com/blog/${post.slug}`
      }
    ]
  };

  const relatedPosts = getRelatedPosts(post.slug, post.category);

  return (
    <>
      <SEOHead
        title={post.seo_title || post.title}
        description={post.seo_description || post.excerpt}
        keywords={post.seo_keywords || undefined}
        canonicalUrl={`/blog/${post.slug}`}
        ogImage={heroReady ? post.featured_image || undefined : undefined}
        isArticle={true}
        structuredData={[articleStructuredData, breadcrumbStructuredData]}
      />

      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">

          <div className="max-w-3xl mx-auto mt-8">
            <Link to="/blog">
              <Button variant="outline" size="sm" className="mb-6">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Blog
              </Button>
            </Link>

            <article>
              {/* Header */}
              <header className="mb-8">
                <div className="flex items-center gap-2 mb-3">
                  {post.category && (
                    <Badge variant="secondary" className="text-xs">
                      {formatCategoryName(post.category)}
                    </Badge>
                  )}
                  <span className="text-sm text-muted-foreground flex items-center gap-1">
                    <Calendar className="w-3.5 h-3.5" />
                    {formatDate(post.published_at)}
                  </span>
                </div>

                <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-6 leading-tight">
                  {post.title}
                </h1>

                {post.featured_image && (
                  <div className="mb-8">
                    <BlogFeaturedImage
                      src={post.featured_image}
                      alt={post.featured_image_alt || post.title}
                      frameClassName="aspect-video rounded-lg"
                      imageClassName="w-full h-full object-cover"
                      width={1080}
                      quality={80}
                      loading="eager"
                      fetchPriority="high"
                      sizes="(max-width: 768px) 100vw, 768px"
                    />
                    {post.featured_image_credit && (
                      <div className="mt-2 text-xs italic text-muted-foreground [&_a]:underline [&_a]:text-muted-foreground hover:[&_a]:text-foreground [&_p]:m-0">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>
                          {post.featured_image_credit}
                        </ReactMarkdown>
                      </div>
                    )}
                  </div>
                )}

                {post.excerpt && (
                  <p className="text-lg text-muted-foreground leading-relaxed border-l-4 border-primary pl-6">
                    {post.excerpt}
                  </p>
                )}
              </header>

              {/* Content */}
              <div className="prose prose-lg prose-slate dark:prose-invert max-w-none prose-headings:text-foreground prose-p:text-foreground prose-a:text-foreground prose-li:text-foreground prose-strong:text-foreground prose-code:text-foreground prose-blockquote:text-muted-foreground prose-img:rounded-lg prose-img:shadow-lg prose-figcaption:text-center prose-figcaption:italic prose-figcaption:text-muted-foreground prose-figcaption:text-sm prose-figcaption:mt-2">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  rehypePlugins={[rehypeRaw]}
                  components={{
                    table: ({ node, ...p }) => (
                      <div className="overflow-x-auto">
                        <table {...p} />
                      </div>
                    ),
                    img: ({ src, alt, ...props }) => {
                      if (!src) return null;
                      const srcStr = String(src);
                      const sizes = srcStr.includes('milky-way-planner-apps-compared-2.webp')
                        ? '(max-width: 640px) 60vw, 280px'
                        : '(max-width: 768px) 100vw, 768px';
                      return (
                        <OptimizedImage
                          src={srcStr}
                          alt={alt || ''}
                          width={1080}
                          quality={80}
                          sizes={sizes}
                          className="w-full rounded-lg shadow-lg"
                          {...props}
                        />
                      );
                    },
                  }}
                >
                  {post.content}
                </ReactMarkdown>
              </div>
            </article>

            {/* CTAs — Plan, Workshops, Premium */}
            <div className="mt-14 grid grid-cols-1 sm:grid-cols-3 gap-4">
              <Link to="/planner" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Target className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-white">Plan Your Shoot</h4>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-3">
                  Free Milky Way calendar with nightly visibility ratings.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  Open Planner <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              <Link to="/workshops" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                <div className="flex items-center gap-2 mb-2">
                  <Camera className="w-5 h-5 text-gold" />
                  <h4 className="font-semibold text-white">Browse Workshops</h4>
                </div>
                <p className="text-sm text-white/60 leading-relaxed mb-3">
                  Astrophotography workshops at dark sky locations.
                </p>
                <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                  Find a Workshop <ArrowRight className="w-3.5 h-3.5" />
                </span>
              </Link>
              {!showSubscriberCTA ? (
                <Link to="/features" className="group p-6 rounded-xl bg-gold/10 border border-gold/20 hover:bg-gold/15 transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-gold" />
                    <h4 className="font-semibold text-foreground">Go Premium</h4>
                  </div>
                  <p className="text-sm text-foreground leading-relaxed mb-3">
                    Moon Planner, Trip Planner, exports — $25/year.
                  </p>
                  <span className="text-sm font-semibold text-gold-foreground group-hover:text-gold-dark transition-colors inline-flex items-center gap-1">
                    See Premium, $25/year <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              ) : (
                <Link to="/workshops/instructors" className="group p-6 rounded-xl bg-[#0f1923] hover:bg-[#1a2b3d] transition-colors">
                  <div className="flex items-center gap-2 mb-2">
                    <Star className="w-5 h-5 text-gold" />
                    <h4 className="font-semibold text-white">Meet Instructors</h4>
                  </div>
                  <p className="text-sm text-white/60 leading-relaxed mb-3">
                    Browse instructor profiles and find your next workshop.
                  </p>
                  <span className="text-sm font-semibold text-gold group-hover:text-gold-light transition-colors inline-flex items-center gap-1">
                    View Instructors <ArrowRight className="w-3.5 h-3.5" />
                  </span>
                </Link>
              )}
            </div>

            {/* Related Articles */}
            {relatedPosts.length > 0 && (
              <div className="mt-16">
                <div className="flex items-center gap-4 mb-8">
                  <div className="flex-1 h-px bg-border" />
                  <span className="text-xs text-muted-foreground uppercase tracking-widest">Keep Reading</span>
                  <div className="flex-1 h-px bg-border" />
                </div>

                <div className="space-y-4">
                  {relatedPosts.map((related) => (
                    <Link
                      key={related.slug}
                      to={`/blog/${related.slug}`}
                      className="flex gap-4 group items-center"
                    >
                      {related.featured_image && (
                        <BlogFeaturedImage
                          src={related.featured_image}
                          alt={related.title}
                          frameClassName="w-20 h-20 flex-shrink-0 rounded-lg"
                          imageClassName="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                          width={160}
                          compact
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-foreground group-hover:text-primary transition-colors line-clamp-1 text-[15px]">
                          {related.title}
                        </h3>
                        <p className="text-sm text-muted-foreground line-clamp-1 mt-0.5">{related.excerpt}</p>
                      </div>
                      <ArrowLeft className="w-4 h-4 text-muted-foreground group-hover:text-primary transition-colors rotate-180 flex-shrink-0 hidden sm:block" />
                    </Link>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Newsletter — full width, flows into footer */}
      <NewsletterSignup variant="blog" />
    </>
  );
};

export default BlogPost;
